import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { API_URL } from 'src/app/core/constants/api';
import { environment } from 'src/environments/environment';
import {
  CacheReloadMaster,
  SaveWabaRequestPayload,
  SaveWabaResponse,
  WabaMasterConfig,
} from '../interface/create-waba.interface';
import { Observable, lastValueFrom } from 'rxjs';
import { CommonService } from 'src/app/shared/services/common.service';
import {
  ISenderEmailPayload,
  ISenderEmailResponse,
} from '../interface/create-sender-email.interface';
import { SenderEmailMasterConfig } from '../interface/sender-email-domain.interface';

@Injectable({
  providedIn: 'root',
})
export class ConfigurationService {
  wabaMasterConfig: Promise<WabaMasterConfig>;
  wabaMasterConfigMap: any = {};
  emailMasterConfig: Promise<SenderEmailMasterConfig>;

  constructor(
    private readonly http: HttpClient,
    private readonly commonService: CommonService
  ) {}

  saveWabaDetails(
    payload: SaveWabaRequestPayload
  ): Observable<SaveWabaResponse> {
    return this.http.post<SaveWabaResponse>(
      `${environment.CAMPAIGN_BASE_URL}${API_URL.SAVE_WABA}`,
      payload
    );
  }

  getMasterDataFromAPI(tab: string) {
    return lastValueFrom(
      this.http.get(
        `${environment.CAMPAIGN_BASE_URL}${API_URL.ADMIN_CONFIG}?tab=${tab}`
      )
    );
  }

  async callMasterData(tab: string) {
    const wabaMasterConfig = await this.wabaMasterConfig;
    if (!wabaMasterConfig || Object.keys(wabaMasterConfig).length <= 0) {
      const response: any = await this.getMasterDataFromAPI(tab).catch(
        (error: HttpErrorResponse) => {
          this.commonService.showError(error);
        }
      );
      if (response && !response.error) {
        this.prepareMasterDataMap(response.data);
        this.wabaMasterConfig = response.data;
      }
    }
    return this.wabaMasterConfig;
  }

  prepareMasterDataMap(config: WabaMasterConfig) {
    const { bsp, wabaType } = config;
    const bspMap: { [key: string]: string } = {};
    const wabaTypeMap: { [key: string]: string | { [key: string]: string } } =
      {};
    for (const bspObj of bsp) {
      bspMap[bspObj.code] = bspObj.value;
    }
    const wabaSubTypeMap: { [key: string]: string } = {};
    for (const wabaTypeObj of wabaType) {
      if (wabaTypeObj.wabaSubType?.length) {
        for (const wabaSubType of wabaTypeObj.wabaSubType) {
          wabaSubTypeMap[wabaSubType.code] = wabaSubType.value;
        }
      }
      wabaTypeMap[wabaTypeObj.code] = wabaTypeObj.value;
    }
    wabaTypeMap['wabaSubTypeMap'] = wabaSubTypeMap;
    this.wabaMasterConfigMap = {
      bspMap,
      wabaTypeMap,
    };
  }

  getWabaMasterMap() {
    return this.wabaMasterConfigMap;
  }

  updateWabaStatus(payload: {
    wabaCode: string;
    active: boolean;
    statusReason: string;
  }) {
    return this.http.patch(
      `${environment.CAMPAIGN_BASE_URL}${API_URL.UPDATE_WABA_STATUS}`,
      payload
    );
  }

  testWabaNumber(wabaCode: string, recipientId: string) {
    return this.http.post(
      `${environment.CAMPAIGN_BASE_URL}${API_URL.TEST_WABA}?wabaCode=${wabaCode}`,
      { recipientId }
    );
  }

  getCacheListData(): Observable<any> {
    return this.http.get<any>(
      `${environment.CAMPAIGN_BASE_URL}${API_URL.MASTER_CACHE_RELOAD}`
    );
  }

  callCacheAPI(apiProperties: CacheReloadMaster): Observable<any> {
    if (apiProperties.methodType?.trim()?.toLowerCase() === 'get') {
      return this.http.get<any>(apiProperties.path);
    } else if (apiProperties.methodType?.trim()?.toLowerCase() === 'post') {
      return this.http.post<any>(apiProperties.path, {});
    } else {
      throw new Error('Unsupported method type');
    }
  }

  createSenderEmail(
    payload: ISenderEmailPayload
  ): Observable<ISenderEmailResponse> {
    return this.http.post<ISenderEmailResponse>(
      `${environment.CAMPAIGN_BASE_URL}${API_URL.SENDER_EMAIL_REGISTER}`,
      payload
    );
  }

  async getEmailMasterData(tab: string) {
    const emailMasterConfig = await this.emailMasterConfig;
    if (!emailMasterConfig || Object.keys(emailMasterConfig).length <= 0) {
      const response: any = await this.getMasterDataFromAPI(tab).catch(
        (error: HttpErrorResponse) => {
          this.commonService.showError(error);
        }
      );
      if (response && !response.error) {
        this.emailMasterConfig = response.data;
      }
    }
    return this.emailMasterConfig;
  }

  updateSenderEmailStatus(payload: {
    code: string;
    active: boolean;
    statusReason: string;
  }) {
    return this.http.patch(
      `${environment.CAMPAIGN_BASE_URL}${API_URL.UPDATE_SENDER_EMAIL_STATUS}`,
      payload
    );
  }

  testSenderEmail(senderEmailCode: string, recipientEmailId: string) {
    return this.http.post(
      `${environment.CAMPAIGN_BASE_URL}${API_URL.TEST_SENDER_EMAIL}?code=${senderEmailCode}&recipientId=${recipientEmailId}`,
      ''
    );
  }
}
